/**
 * @file        src/pages/faq/FaqList.jsx
 * @description FAQ List. Columns: # · Question · Answer · Brand · Order ·
 *              Status · Action. Backed by /api/faqs. Clone of the testimonial
 *              list plus a brand filter (registry-driven, never hardcoded).
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (FAQ Management, CR A2).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import BrandSelect from '../../components/BrandSelect';
import { useBrands } from '../../api/brands';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

// Characters of the answer shown before "…more" — about two lines in the
// 420px column, which is what the row was sized for.
const ANSWER_LIMIT = 110;

export default function FaqList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [brand, setBrand] = useState('');
  // Rows whose answer is expanded in place. This replaces the native `title`
  // tooltip, which the browser renders as an unstyled OS bubble over the table —
  // it is not part of the console's UI and cannot be made to match it.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const qc = useQueryClient();
  const { labelOf } = useBrands();
  const params = { page, pageSize };
  if (applied) params.q = applied;
  if (brand) params.brand = brand;

  const { data, isLoading } = useQuery({
    queryKey: qk.faqs.list(params),
    queryFn: () => get('/faqs', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/faqs/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'FAQ removed.');
      qc.invalidateQueries({ queryKey: ['faqs'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    if (!(await confirm(`Delete FAQ “${row.question}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="FAQ List"
        subtitle="FAQ Management"
        actions={<Link to="/faqs/new" className="btn primary"><Icon name="plus" /> Add FAQ</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Question / Answer</label>
            <input placeholder="Search FAQ…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Brand</label>
            <BrandSelect value={brand} onChange={(v) => { setPage(1); setBrand(v); }} withAll />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Question</th><th>Answer</th><th>Brand</th><th>Order</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No FAQs yet. Add your first one.</td></tr>
                ) : rows.map((f, idx) => (
                  // The answer makes this row several lines tall, so every other
                  // cell must sit in the MIDDLE of it — otherwise Status and the
                  // icons float against the top edge while the text runs past them.
                  <tr key={f.id} style={{ verticalAlign: 'middle' }}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>
                      {/* `.table tbody td` sets white-space:nowrap, so a long
                          question could not wrap at ANY width without this. */}
                      <b style={{ display: 'block', width: 280, maxWidth: 280, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {f.question}
                      </b>
                    </td>
                    <td>
                      {(() => {
                        const full = f.answer || '';
                        const isOpen = expanded.has(f.id);
                        const long = full.length > ANSWER_LIMIT;
                        // Truncated by CHARACTER count, not a CSS line-clamp: a
                        // clamped block hides its overflow, so a link placed after it
                        // would drop onto its own line. Slicing lets "…more" finish
                        // the sentence inline, exactly where the text stops.
                        const shown = !long || isOpen ? full : `${full.slice(0, ANSWER_LIMIT).trimEnd()}…`;
                        if (!full) return <span className="muted">—</span>;
                        return (
                          <div
                            style={{
                              width: 420,
                              maxWidth: 420,
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              fontSize: 13,
                              lineHeight: 1.6,
                              color: 'var(--text)',
                            }}
                          >
                            {shown}
                            {long && (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() => toggleRow(f.id)}
                                aria-expanded={isOpen}
                                style={{ marginLeft: 4 }}
                              >
                                {isOpen ? 'less' : 'more'}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>{labelOf(f.brand)}</td>
                    <td className="mono" style={{ verticalAlign: 'middle' }}>{f.display_order}</td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <Chip tone={f.is_active ? 'ok' : 'rej'}>{f.is_active ? 'Active' : 'Inactive'}</Chip>
                    </td>
                    {/* NOT `.row-act` on the <td>: that class is display:flex, which
                        makes the CELL a flex container and discards the table's
                        vertical-align — so the icons sat higher than the Active chip
                        next to them. Keep the cell a normal table-cell (centred) and
                        put the flex row on a div INSIDE it. */}
                    <td style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Link to={`/faqs/${f.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                        <button className="ib del" aria-label="Delete" onClick={() => onDelete(f)}><Icon name="trash" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
